import { useCallback, useEffect, useRef } from 'react';
import { ApiError } from '../lib/api';
import { ensureAnonId, forgetAnonId, postView } from '../queries/share';

/**
 * The view beacon.
 *
 * One row per viewer per video, updated in place, so this can fire often
 * without inflating anything. It reports the furthest point reached, which is
 * what makes "watched 80% of it" a real number on the dashboard rather than a
 * count of people who opened a link and left, and the stretches actually
 * played, which tell a watch to the end apart from a drag to the end.
 *
 * Fires: once on first play, every 10 seconds of playback, on pause, and when
 * the tab goes away. Never while paused, and never twice for the same second.
 */

const INTERVAL_MS = 10_000;
const COMPLETION_RATIO = 0.95;
/**
 * The playhead moves this far between two progress reports only by jumping:
 * reports come four times a second, and even at double speed that is half a
 * second of footage each.
 */
const JUMP_SEC = 2;

export function useViewBeacon(shareId: string, enabled: boolean) {
  const lastSentRef = useRef(-1);
  const progressRef = useRef(0);
  const durationRef = useRef(0);
  const completedRef = useRef(false);
  const inFlightRef = useRef(false);
  // Each run of uninterrupted playback as [start, end]; a new one opens
  // whenever the playhead jumps. Sent whole each time, merged: the server
  // keeps the union with earlier sittings.
  const rangesRef = useRef<Array<[number, number]>>([]);
  const lastTimeRef = useRef<number | null>(null);
  const lastSentRangesRef = useRef('');

  const send = useCallback(
    (force: boolean) => {
      if (!enabled || inFlightRef.current) return;

      const progress = Math.floor(progressRef.current);
      const duration = durationRef.current;
      const completed =
        duration > 0 && progress >= duration * COMPLETION_RATIO ? true : completedRef.current;
      const ranges = rangesRef.current
        .filter(([start, end]) => end - start >= 0.25)
        .map(([start, end]): [number, number] => [Math.round(start * 10) / 10, Math.round(end * 10) / 10]);
      const rangesKey = JSON.stringify(ranges);

      if (
        !force &&
        progress === lastSentRef.current &&
        completed === completedRef.current &&
        rangesKey === lastSentRangesRef.current
      ) {
        return;
      }

      lastSentRef.current = progress;
      completedRef.current = completed;
      lastSentRangesRef.current = rangesKey;
      inFlightRef.current = true;

      void ensureAnonId()
        .then((anonId) =>
          postView(shareId, {
            anonId,
            progressSec: progress,
            completed,
            ...(ranges.length > 0 ? { ranges } : {}),
          }),
        )
        .catch((error: unknown) => {
          // An id the API no longer recognises is dropped, and the next
          // beacon mints a fresh one. Anything else: a view that does not
          // record is not worth telling anybody about.
          if (error instanceof ApiError && error.code === 'anon_id_required') forgetAnonId();
        })
        .finally(() => {
          inFlightRef.current = false;
        });
    },
    [enabled, shareId],
  );

  /** Call from the player's progress handler. */
  const report = useCallback((currentTime: number, duration: number) => {
    progressRef.current = Math.max(progressRef.current, currentTime);
    if (Number.isFinite(duration) && duration > 0) durationRef.current = duration;
    // Still moving forward in small steps: the run in progress reaches to
    // here. Anything else (the first report, a jump, a step back) opens a
    // new run from here.
    const last = lastTimeRef.current;
    lastTimeRef.current = currentTime;
    const ranges = rangesRef.current;
    const open = ranges[ranges.length - 1];
    if (open && last !== null && currentTime >= last && currentTime - last <= JUMP_SEC) {
      open[1] = currentTime;
    } else {
      ranges.push([currentTime, currentTime]);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // The open itself counts as a view, even if they never press play.
    send(true);
    const timer = window.setInterval(() => send(false), INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') send(true);
    };
    const onPageHide = () => send(true);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      send(true);
    };
  }, [enabled, send]);

  return { report, flush: () => send(true) };
}
